import { Button, Flex, ScrollArea, Text } from '@mantine/core';
import { useMemo } from 'react';
import { COLUMN_HEIGHT, ExplorerPanel } from './ExplorerParts';
import type { ModuleIndex } from './module-filter';
import './ModuleExplorer.css';
import { NO_MODULES } from './openapi-utils';
import { buildSpecTree, type SpecNode } from './spec-tree';
import { useSpecTreeNavigation } from './spec-tree-navigation';
import SpecNodeDetails from './SpecNodeDetails';
import SpecTreeView from './SpecTreeView';

type OutputExplorerProps = {
  spec: Record<string, unknown>;
  moduleIndex: ModuleIndex;
};

// Open each operation and its response, so the top level output fields are visible straight away
const startsExpanded = (node: SpecNode) => node.kind === 'operation' || node.kind === 'response';

/** Browses the schema of what each calculator operation returns, with details for the selected field */
function OutputExplorer({ spec, moduleIndex }: OutputExplorerProps) {
  const specTree = useMemo(() => buildSpecTree(spec, moduleIndex, 'outputs'), [spec, moduleIndex]);
  const { tree, selectedNode, treeViewport, detailsViewport, navigateTo } = useSpecTreeNavigation(
    specTree,
    startsExpanded,
  );

  return (
    <Flex gap="md" h={COLUMN_HEIGHT} className="module-explorer">
      <ExplorerPanel
        title="Output schema"
        flex={1}
        miw={0}
        actions={
          <>
            <Button variant="subtle" size="compact-xs" onClick={() => tree.expandAllNodes()}>
              Expand all
            </Button>
            <Button variant="subtle" size="compact-xs" onClick={() => tree.collapseAllNodes()}>
              Collapse all
            </Button>
          </>
        }
      >
        <ScrollArea flex={1} p="xs" viewportRef={treeViewport}>
          <SpecTreeView specTree={specTree} tree={tree} scrollRef={treeViewport} />
        </ScrollArea>
      </ExplorerPanel>

      <ExplorerPanel title="Details" flex={1} miw={0}>
        <ScrollArea flex={1} p="md" viewportRef={detailsViewport}>
          {selectedNode ? (
            <SpecNodeDetails
              node={selectedNode}
              nodes={specTree.nodes}
              highlights={null}
              selectedModules={NO_MODULES}
              onNavigate={navigateTo}
            />
          ) : (
            <Text c="dimmed" size="sm">
              Select a node in the output schema to see its details.
            </Text>
          )}
        </ScrollArea>
      </ExplorerPanel>
    </Flex>
  );
}

export default OutputExplorer;
